import styled, { createGlobalStyle, css } from 'styled-components';

import { withAlpha } from 'services/ThemeTokens';
import { TNotificationTone } from './notificationTypes';

/**
 * Вёрстка колокольчика (ТЗ 0068).
 *
 * `z-index` здесь НЕ пишется нигде и писать его не нужно: панель живёт в
 * поповере antd, а тот уходит в `document.body` со своим слоем выше шапки.
 * Голое число больше 100 вне allow-list роняет сторож `layers.guard`.
 */

/** Кнопка-колокольчик. Копия `PaletteTrigger` из шапки: две иконочные кнопки
 *  рядом обязаны выглядеть одинаково, иначе шапка «рассыпается». */
export const BellButton = styled.button<{ $active: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'var(--k-border-0d53fc)' : 'var(--k-border-dfe6f1)'};
  border-radius: 8px;
  cursor: pointer;
  color: ${({ $active }) =>
    $active ? 'var(--k-fg-0d53fc)' : 'var(--k-fg-616e78)'};
  background: var(--k-bg-ffffff);
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: var(--k-fg-0d53fc);
    border-color: var(--k-border-0d53fc);
  }
`;

/** Накладной счётчик — по образцу `MiniBadge` коммуникатора. */
export const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--k-bg-2563eb);
  color: var(--k-fg-ffffff);
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--k-border-ffffff);
`;

/** Янтарная точка: звук включён, но браузер его ещё не разрешил. */
export const LockedDot = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--k-bg-d97706);
  border: 2px solid var(--k-border-ffffff);
`;

export const PopoverStyles = createGlobalStyle`
  .notification-center-popover .ant-popover-inner-content {
    padding: 0;
  }
  .notification-center-popover .ant-popover-arrow {
    display: none;
  }
`;

export const Panel = styled.div`
  width: 420px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  max-height: min(70vh, 560px);
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--k-border-ebeff2);
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--k-fg-263446);
`;

export const CountPill = styled.span`
  min-width: 20px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--k-bg-2563eb);
  color: var(--k-fg-ffffff);
  font-size: 11px;
  font-weight: 700;
`;

export const HeadSpacer = styled.div`
  flex: 1;
`;

export const TextButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: var(--k-fg-0d53fc);
  font-size: 12px;

  &:disabled {
    color: var(--k-fg-616e78);
    cursor: default;
  }
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 2px;
  cursor: pointer;
  color: var(--k-fg-616e78);

  &:hover {
    color: var(--k-fg-0d53fc);
  }
`;

export const Tabs = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--k-border-ebeff2);
`;

export const Tab = styled.button<{ $active: boolean }>`
  border: none;
  cursor: pointer;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  background: ${({ $active }) => ($active ? 'var(--k-bg-f2f5f9)' : 'none')};
  color: ${({ $active }) =>
    $active ? 'var(--k-fg-263446)' : 'var(--k-fg-616e78)'};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
`;

export const Warning = styled.div`
  padding: 8px 14px;
  background: var(--k-bg-fff7ed);
  color: var(--k-fg-92400e);
  font-size: 12px;
  border-bottom: 1px solid var(--k-border-fed7aa);
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const List = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const TONE_COLOR: Record<TNotificationTone, string> = {
  accent: 'var(--k-fg-2563eb)',
  warn: 'var(--k-fg-d97706)',
  danger: 'var(--k-fg-dc2626)',
};

export const Row = styled.button<{ $unread: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 10px;
  align-items: start;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid var(--k-border-ebeff2);
  cursor: pointer;
  text-align: left;
  background: ${({ $unread }) =>
    $unread ? 'var(--k-bg-f4f7ff)' : 'var(--k-bg-ffffff)'};

  &:hover {
    background: var(--k-bg-f2f5f9);
  }
`;

export const RowIcon = styled.span<{ $tone: TNotificationTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: ${({ $tone }) => TONE_COLOR[$tone]};
  background: ${({ $tone }) => withAlpha(TONE_COLOR[$tone], '14')};
`;

export const RowBody = styled.div`
  min-width: 0;
`;

export const RowTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--k-fg-263446);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowText = styled.div`
  font-size: 12px;
  color: var(--k-fg-616e78);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const RowTime = styled.div`
  font-size: 11px;
  color: var(--k-fg-616e78);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const Empty = styled.div`
  padding: 32px 14px;
  text-align: center;
  color: var(--k-fg-616e78);
  font-size: 13px;
`;

export const PanelFoot = styled.div`
  padding: 10px 14px;
  border-top: 1px solid var(--k-border-ebeff2);
  display: flex;
  align-items: center;
  gap: 12px;
`;

/* ── Настройки ─────────────────────────────────────────────────────────── */

export const Pane = styled.div`
  padding: 14px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Field = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: var(--k-fg-263446);
`;

export const FieldHint = styled.div`
  font-size: 11px;
  color: var(--k-fg-616e78);
  margin-top: 2px;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${() => css`
    & + & {
      padding-top: 12px;
      border-top: 1px solid var(--k-border-ebeff2);
    }
  `}
`;

export const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--k-fg-616e78);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const Saved = styled.div`
  font-size: 12px;
  color: var(--k-fg-256e29);
`;

export const Failed = styled.div`
  font-size: 12px;
  color: var(--k-fg-c62828);
`;

export const Mono = styled.div`
  font-family: SFMono-Regular, Consolas, monospace;
  font-size: 11px;
  background: var(--k-bg-f2f5f9);
  border-radius: 6px;
  padding: 8px;
  word-break: break-all;
  color: var(--k-fg-263446);
`;

export const InlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
